import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { 
  Shield, AlertTriangle, Clock, RefreshCcw, FileText, 
  TrendingUp, TrendingDown, Download, BarChart3, Target,
  FileWarning, CheckCircle2, XCircle, AlertCircle, Loader2,
  Building2, Scale, MapPin, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { AppLayout } from '../layouts/AppLayout';

const IntegrityProgramPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboardData, setDashboardData] = useState(null);
  const [riskMap, setRiskMap] = useState(null);
  const [criticalActs, setCriticalActs] = useState([]);
  const [recurringActs, setRecurringActs] = useState([]);
  const [delayedActs, setDelayedActs] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [mitigationPlans, setMitigationPlans] = useState([]);
  const [evidences, setEvidences] = useState([]);
  const [generatingReport, setGeneratingReport] = useState(false);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await api.get('/integrity/dashboard');
      setDashboardData(res.data);
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
    }
  }, [api]);

  const fetchRiskMap = useCallback(async () => {
    try {
      const res = await api.get('/integrity/risk-map');
      setRiskMap(res.data);
    } catch (error) {
      console.error('Erro ao carregar mapa de riscos:', error);
    }
  }, [api]);

  const fetchCriticalActs = useCallback(async () => {
    try {
      const res = await api.get('/integrity/critical-acts');
      setCriticalActs(res.data.acts || []);
    } catch (error) {
      console.error('Erro ao carregar atos críticos:', error);
    }
  }, [api]);

  const fetchRecurringActs = useCallback(async () => {
    try {
      const res = await api.get('/integrity/recurring-acts');
      setRecurringActs(res.data.acts || []);
    } catch (error) {
      console.error('Erro ao carregar atos reincidentes:', error);
    }
  }, [api]);

  const fetchDelayedActs = useCallback(async () => {
    try {
      const res = await api.get('/integrity/delayed-acts');
      setDelayedActs(res.data.acts || []);
    } catch (error) {
      console.error('Erro ao carregar atos atrasados:', error);
    }
  }, [api]);

  const fetchIncidents = useCallback(async () => {
    try {
      const res = await api.get('/integrity/incidents');
      setIncidents(res.data.incidents || []);
    } catch (error) {
      console.error('Erro ao carregar incidentes:', error);
    }
  }, [api]);

  const fetchMitigationPlans = useCallback(async () => {
    try {
      const res = await api.get('/integrity/mitigation-plans');
      setMitigationPlans(res.data.plans || []);
    } catch (error) {
      console.error('Erro ao carregar planos:', error);
    }
  }, [api]);

  const fetchEvidences = useCallback(async () => {
    try {
      const res = await api.get('/integrity/evidences');
      setEvidences(res.data.evidences || []);
    } catch (error) {
      console.error('Erro ao carregar evidências:', error);
    }
  }, [api]);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([
        fetchDashboard(),
        fetchRiskMap(),
        fetchCriticalActs(),
        fetchRecurringActs(),
        fetchDelayedActs(),
        fetchIncidents(),
        fetchMitigationPlans(),
        fetchEvidences()
      ]);
      setLoading(false);
    };
    loadAll();
  }, [fetchDashboard, fetchRiskMap, fetchCriticalActs, fetchRecurringActs, fetchDelayedActs, fetchIncidents, fetchMitigationPlans, fetchEvidences]);

  const generateReport = async (formato = 'pdf') => {
    setGeneratingReport(true);
    try {
      const res = await api.post('/integrity/report/audit', { formato }, {
        responseType: formato === 'pdf' ? 'blob' : 'json'
      });
      
      if (formato === 'pdf') {
        const blob = new Blob([res.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `relatorio_integridade_${new Date().toISOString().split('T')[0]}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success('Relatório PDF gerado com sucesso');
      } else {
        toast.success('Relatório JSON gerado');
      }
    } catch (error) {
      toast.error('Erro ao gerar relatório');
      console.error(error);
    } finally {
      setGeneratingReport(false);
    }
  };

  const getRiskColor = (level) => {
    switch (level) {
      case 'CRITICO': return 'bg-red-500';
      case 'ALTO': return 'bg-orange-500';
      case 'MEDIO': return 'bg-yellow-500';
      case 'BAIXO': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusBadge = (status) => {
    const configs = {
      'CRITICAL': { color: 'bg-red-100 text-red-800', label: 'Crítico' },
      'HIGH': { color: 'bg-orange-100 text-orange-800', label: 'Alto' },
      'MEDIUM': { color: 'bg-yellow-100 text-yellow-800', label: 'Médio' },
      'LOW': { color: 'bg-green-100 text-green-800', label: 'Baixo' },
      'ATRASADO': { color: 'bg-red-100 text-red-800', label: 'Atrasado' },
      'EM_ANDAMENTO': { color: 'bg-blue-100 text-blue-800', label: 'Em Andamento' },
      'PENDENTE': { color: 'bg-yellow-100 text-yellow-800', label: 'Pendente' }
    };
    const config = configs[status] || { color: 'bg-gray-100 text-gray-800', label: status };
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#1d4ed8]" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-6 space-y-6" data-testid="integrity-program-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Shield className="w-7 h-7 text-[#1d4ed8]" />
              Programa de Integridade
            </h1>
            <p className="text-slate-500 mt-1">
              Instrumento de Conformidade e Governança Institucional
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => generateReport('json')}
              disabled={generatingReport}
              data-testid="export-json-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Exportar JSON
            </Button>
            <Button
              onClick={() => generateReport('pdf')}
              disabled={generatingReport}
              className="bg-[#1d4ed8] hover:bg-[#5835CC]"
              data-testid="generate-report-btn"
            >
              {generatingReport ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <FileText className="w-4 h-4 mr-2" />
              )}
              Relatório para Auditoria
            </Button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-red-500" data-testid="kpi-critical">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Atos Críticos</p>
                  <p className="text-3xl font-bold text-slate-900">{dashboardData?.total_atos_criticos || 0}</p>
                </div>
                <AlertTriangle className="w-10 h-10 text-red-500" />
              </div>
              <p className="text-xs text-slate-400 mt-2">Risco identificado elevado</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-orange-500" data-testid="kpi-recurring">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Reincidentes</p>
                  <p className="text-3xl font-bold text-slate-900">{dashboardData?.total_atos_reincidentes || 0}</p>
                </div>
                <RefreshCcw className="w-10 h-10 text-orange-500" />
              </div>
              <p className="text-xs text-slate-400 mt-2">Fragilidade sistêmica</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-yellow-500" data-testid="kpi-delayed">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Em Atraso</p>
                  <p className="text-3xl font-bold text-slate-900">{dashboardData?.total_atos_atrasados || 0}</p>
                </div>
                <Clock className="w-10 h-10 text-yellow-500" />
              </div>
              <p className="text-xs text-slate-400 mt-2">Risco de sanção</p>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-[#1d4ed8]" data-testid="kpi-compliance">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Taxa de Conformidade</p>
                  <p className="text-3xl font-bold text-slate-900">{dashboardData?.percentual_conformidade || 0}%</p>
                </div>
                <Target className="w-10 h-10 text-[#1d4ed8]" />
              </div>
              <Progress value={dashboardData?.percentual_conformidade || 0} className="mt-2 h-2" />
            </CardContent>
          </Card>
        </div>

        {/* Risk Index */}
        <Card data-testid="risk-index-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[#1d4ed8]" />
              Índice de Risco Institucional
            </CardTitle>
            <CardDescription>
              Métrica consolidada de exposição a riscos (escala 0-10)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <div className="h-4 bg-gradient-to-r from-green-500 via-yellow-500 to-red-500 rounded-full relative">
                  <div 
                    className="absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white border-2 border-slate-800 rounded-full shadow-lg transition-all duration-500"
                    style={{ left: `calc(${(dashboardData?.indice_risco_institucional || 0) * 10}% - 8px)` }}
                  />
                </div>
                <div className="flex justify-between mt-1 text-xs text-slate-500">
                  <span>Baixo (0)</span>
                  <span>Médio (5)</span>
                  <span>Alto (10)</span>
                </div>
              </div>
              <div className="text-right">
                <p className="text-4xl font-bold text-slate-900">{dashboardData?.indice_risco_institucional || 0}</p>
                <p className="text-sm text-slate-500">de 10</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid grid-cols-5 w-full max-w-3xl">
            <TabsTrigger value="dashboard" data-testid="tab-dashboard">
              <BarChart3 className="w-4 h-4 mr-1" /> Visão Geral
            </TabsTrigger>
            <TabsTrigger value="risk-map" data-testid="tab-risk-map">
              <MapPin className="w-4 h-4 mr-1" /> Mapa de Riscos
            </TabsTrigger>
            <TabsTrigger value="incidents" data-testid="tab-incidents">
              <FileWarning className="w-4 h-4 mr-1" /> Incidentes
            </TabsTrigger>
            <TabsTrigger value="mitigation" data-testid="tab-mitigation">
              <Target className="w-4 h-4 mr-1" /> Mitigação
            </TabsTrigger>
            <TabsTrigger value="evidences" data-testid="tab-evidences">
              <FileText className="w-4 h-4 mr-1" /> Evidências
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Evolução Mensal */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Evolução Mensal de Riscos</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {dashboardData?.evolucao_mensal?.map((mes, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <span className="w-20 text-sm text-slate-500">{mes.mes_nome}</span>
                        <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-[#1d4ed8] to-[#9333EA] transition-all duration-500"
                            style={{ width: `${mes.indice_risco * 10}%` }}
                          />
                        </div>
                        <span className="w-12 text-right text-sm font-medium">{mes.indice_risco}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Atos Críticos */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center justify-between">
                    Atos Críticos Ativos
                    <Badge variant="destructive">{criticalActs.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {criticalActs.slice(0, 5).map((ato) => (
                      <div key={ato.act_id} className="flex items-center justify-between p-2 bg-red-50 rounded-lg">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">
                            {ato.act_number || ato.act_id.slice(0, 8)}
                          </p>
                          <p className="text-xs text-slate-500 truncate">{ato.control_body}</p>
                        </div>
                        {getStatusBadge(ato.risk_level)}
                      </div>
                    ))}
                    {criticalActs.length === 0 && (
                      <p className="text-sm text-slate-500 text-center py-4">Nenhum ato crítico</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Atos Atrasados e Reincidentes */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-yellow-500" /> Atos em Atraso
                    </span>
                    <Badge className="bg-yellow-100 text-yellow-800">{delayedActs.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {delayedActs.slice(0, 5).map((ato) => (
                      <div key={ato.act_id} className="flex items-center justify-between p-2 bg-yellow-50 rounded-lg">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">
                            {ato.act_number || ato.act_id.slice(0, 8)}
                          </p>
                          <p className="text-xs text-slate-500">
                            Prazo: {new Date(ato.deadline).toLocaleDateString('pt-BR')}
                          </p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      </div>
                    ))}
                    {delayedActs.length === 0 && (
                      <p className="text-sm text-slate-500 text-center py-4">Nenhum ato em atraso</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <RefreshCcw className="w-4 h-4 text-orange-500" /> Atos Reincidentes
                    </span>
                    <Badge className="bg-orange-100 text-orange-800">{recurringActs.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {recurringActs.slice(0, 5).map((ato) => (
                      <div key={ato.act_id} className="flex items-center justify-between p-2 bg-orange-50 rounded-lg">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">
                            {ato.act_number || ato.act_id.slice(0, 8)}
                          </p>
                          <p className="text-xs text-slate-500 truncate">{ato.thematic_area}</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      </div>
                    ))}
                    {recurringActs.length === 0 && (
                      <p className="text-sm text-slate-500 text-center py-4">Nenhum ato reincidente</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Risk Map Tab */}
          <TabsContent value="risk-map" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Matriz de Riscos */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Matriz de Riscos por Categoria</CardTitle>
                  <CardDescription>Classificação conforme metodologia COSO/ISO 31000</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {riskMap?.matriz_riscos?.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                        <div className={`w-3 h-3 rounded-full ${getRiskColor(item.nivel_risco)}`} />
                        <div className="flex-1">
                          <p className="text-sm font-medium">{item.categoria}</p>
                          <p className="text-xs text-slate-500">
                            {item.quantidade_atos} atos ({item.percentual}%)
                          </p>
                        </div>
                        <Badge className={getRiskColor(item.nivel_risco) + ' text-white'}>
                          {item.nivel_risco}
                        </Badge>
                      </div>
                    ))}
                    {(!riskMap?.matriz_riscos || riskMap.matriz_riscos.length === 0) && (
                      <p className="text-sm text-slate-500 text-center py-4">Sem dados para exibir</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Áreas Críticas */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Building2 className="w-4 h-4" /> Áreas com Maior Exposição
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {riskMap?.areas_criticas?.map((area, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 border-b last:border-0">
                        <div>
                          <p className="text-sm font-medium">{area.area}</p>
                          <p className="text-xs text-slate-500">{area.total} atos</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-red-600 font-medium">{area.criticos} críticos</p>
                        </div>
                      </div>
                    ))}
                    {(!riskMap?.areas_criticas || riskMap.areas_criticas.length === 0) && (
                      <p className="text-sm text-slate-500 text-center py-4">Sem dados para exibir</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Órgãos e Temáticas */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Scale className="w-4 h-4" /> Órgãos de Controle com Maior Incidência
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {riskMap?.orgaos_com_maior_incidencia?.map((orgao, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                        <span className="text-sm">{orgao.orgao}</span>
                        <div className="flex gap-2">
                          <Badge variant="outline">{orgao.total} atos</Badge>
                          {orgao.reincidentes > 0 && (
                            <Badge className="bg-orange-100 text-orange-800">{orgao.reincidentes} reinc.</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Temáticas Recorrentes</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {riskMap?.tematicas_recorrentes?.map((tema, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                        <span className="text-sm truncate flex-1">{tema.tematica}</span>
                        <Badge variant="outline">{tema.total}</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Incidents Tab */}
          <TabsContent value="incidents" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Incidentes e Não Conformidades</CardTitle>
                <CardDescription>Registro de ocorrências derivadas de atos críticos</CardDescription>
              </CardHeader>
              <CardContent>
                {incidents.length > 0 ? (
                  <div className="space-y-3">
                    {incidents.map((inc) => (
                      <div key={inc.incident_id} className="p-4 border rounded-lg">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium">{inc.titulo}</p>
                            <p className="text-sm text-slate-500">{inc.descricao}</p>
                          </div>
                          <Badge>{inc.status}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-2" />
                    <p className="text-slate-600">Nenhum incidente registrado</p>
                    <p className="text-sm text-slate-400">O sistema identificará automaticamente não conformidades</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Mitigation Tab */}
          <TabsContent value="mitigation" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Planos de Ação Corretiva</CardTitle>
                <CardDescription>Instrumentos de tratamento de riscos identificados</CardDescription>
              </CardHeader>
              <CardContent>
                {mitigationPlans.length > 0 ? (
                  <div className="space-y-3">
                    {mitigationPlans.map((plan) => (
                      <div key={plan.plan_id} className="p-4 border rounded-lg">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium">{plan.titulo}</p>
                            <p className="text-sm text-slate-500">
                              Responsável: {plan.responsavel}
                            </p>
                          </div>
                          <Badge className={plan.status === 'EM_EXECUCAO' ? 'bg-blue-100 text-blue-800' : ''}>
                            {plan.status}
                          </Badge>
                        </div>
                        {plan.percentual_conclusao !== undefined && (
                          <Progress value={plan.percentual_conclusao} className="mt-2 h-2" />
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Target className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-600">Nenhum plano de mitigação</p>
                    <p className="text-sm text-slate-400">Planos de ação serão consolidados automaticamente</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Evidences Tab */}
          <TabsContent value="evidences" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Evidências Documentais</CardTitle>
                <CardDescription>Documentos comprobatórios anexados aos atos de controle</CardDescription>
              </CardHeader>
              <CardContent>
                {evidences.length > 0 ? (
                  <div className="space-y-2">
                    {evidences.map((ev) => (
                      <div key={ev.evidence_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-slate-400" />
                          <div>
                            <p className="text-sm font-medium">{ev.titulo}</p>
                            <p className="text-xs text-slate-500">{ev.arquivo_nome}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {ev.validado ? (
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                          ) : (
                            <AlertCircle className="w-4 h-4 text-yellow-500" />
                          )}
                          <Badge variant="outline">{ev.tipo}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-600">Nenhuma evidência cadastrada</p>
                    <p className="text-sm text-slate-400">Documentos anexados aos atos aparecerão aqui</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
};

export default IntegrityProgramPage;
